import { useEffect, useRef, useState } from 'react';
import { VideoIcon } from './Icons';

export function VideoRecorder({
  onCapture,
  onUseDeviceCamera,
  filePrefix = 'family-video',
  maxSeconds = 60,
}: {
  onCapture: (file: File) => void;
  onUseDeviceCamera: () => void;
  filePrefix?: string;
  maxSeconds?: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const disposedRef = useRef(false);
  const [status, setStatus] = useState<'starting' | 'ready' | 'recording' | 'error'>('starting');
  const [elapsed, setElapsed] = useState(0);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    disposedRef.current = false;
    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        setCameraError('In-app recording is not available in this browser.');
        setStatus('error');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1080 },
            height: { ideal: 1920 },
          },
          audio: true,
        });
        if (disposedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setStatus('ready');
      } catch {
        if (disposedRef.current) return;
        setCameraError('Camera access was unavailable. You can still use your phone’s camera.');
        setStatus('error');
      }
    };
    void startCamera();

    return () => {
      disposedRef.current = true;
      const recorder = recorderRef.current;
      if (recorder?.state === 'recording') recorder.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (status !== 'recording') return;
    const startedAt = Date.now() - elapsed * 1000;
    const update = () => {
      const nextElapsed = Math.min(maxSeconds, Math.floor((Date.now() - startedAt) / 1000));
      setElapsed(nextElapsed);
      if (nextElapsed >= maxSeconds && recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
    };
    const interval = window.setInterval(update, 250);
    return () => window.clearInterval(interval);
  }, [maxSeconds, status]);

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream || status !== 'ready') return;
    try {
      const candidates = [
        'video/mp4;codecs=h264,aac',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ];
      const mimeType = candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
      const recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 4_000_000,
        audioBitsPerSecond: 128_000,
      });
      recorderRef.current = recorder;
      chunksRef.current = [];
      setElapsed(0);
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      });
      recorder.addEventListener('stop', () => {
        if (disposedRef.current) return;
        const type = recorder.mimeType || chunksRef.current[0]?.type || 'video/mp4';
        const fileType = type.split(';')[0] || 'video/mp4';
        const blob = new Blob(chunksRef.current, { type: fileType });
        if (blob.size === 0) {
          stream.getTracks().forEach((track) => track.stop());
          setCameraError('The recording was empty. Please try again.');
          setStatus('error');
          return;
        }
        stream.getTracks().forEach((track) => track.stop());
        const extension = fileType.includes('webm') ? 'webm' : 'mp4';
        onCapture(new File([blob], `${filePrefix}-${Date.now()}.${extension}`, { type: fileType }));
      }, { once: true });
      recorder.start(1000);
      setStatus('recording');
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      setCameraError('This browser could not start recording. You can still use your phone’s camera.');
      setStatus('error');
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder?.state === 'recording') recorder.stop();
  };

  if (status === 'error') {
    return (
      <div className="story-recorder-error">
        <span><VideoIcon /></span>
        <h3>Use your phone’s camera</h3>
        <p>{cameraError}</p>
        <button type="button" className="btn btn-primary btn-block" onClick={onUseDeviceCamera}>Open phone camera</button>
        <small>After recording, tap OK to return here. You’ll preview the video before sending it.</small>
      </div>
    );
  }

  return (
    <div className={`story-recorder ${status}`}>
      <div className="story-recorder-viewport">
        <video ref={videoRef} muted playsInline autoPlay aria-label="Camera preview" />
        {status === 'starting' && <div className="story-recorder-starting"><div className="spinner" role="status" aria-label="Starting camera" /><span>Starting camera…</span></div>}
        {status === 'recording' && <span className="story-recording-time"><i />{formatDuration(elapsed)} / {formatDuration(maxSeconds)}</span>}
      </div>
      <div className="story-recorder-controls">
        <strong>{status === 'recording' ? 'Recording now' : 'Ready when you are'}</strong>
        <small>{status === 'recording' ? 'Tap the square when you’re finished.' : `Record up to ${maxSeconds} seconds.`}</small>
        <button
          type="button"
          className={`story-record-button ${status === 'recording' ? 'stop' : ''}`}
          disabled={status === 'starting'}
          onClick={status === 'recording' ? stopRecording : startRecording}
          aria-label={status === 'recording' ? 'Stop recording' : 'Start recording'}
        ><span /></button>
      </div>
    </div>
  );
}

export function AutoplayVideo({
  src,
  poster,
  loop = false,
  onEnded,
  label,
}: {
  src: string;
  poster?: string;
  loop?: boolean;
  onEnded?: () => void;
  label: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    void video.play().catch(() => {
      video.muted = true;
      return video.play();
    }).catch(() => undefined);
  }, [src]);

  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      autoPlay
      playsInline
      controls={false}
      disablePictureInPicture
      loop={loop}
      preload="auto"
      onEnded={onEnded}
      onClick={() => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
      }}
      aria-label={label}
    />
  );
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}
